import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, Title, Tooltip, Legend, BarElement } from 'chart.js';
import { dailyDashboard } from '../app/api';
import moment from 'moment';
import 'moment/locale/hy-am';
import { useDashboardContext } from '../context/DashboardContext';

ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, BarElement);

const DashboardIncomeChart = () => {
  const { setHasIncome } = useDashboardContext();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({});

  useEffect(() => {
    const fetchIncomeData = async () => {
      try {
        const response = await dailyDashboard('/dashboard/income');
        const incomeData = response.data;
        const combinedIncome = incomeData?.combined.map(item => {
          const period = Object.keys(item)[0];
          return {
            period,
            done_works: item[period].done_works,
            products: item[period].products,
            total: item[period].total
          };
        });
        const incomeValues = combinedIncome.map(item => item.total);
        const incomeProductValues = combinedIncome.map(item => item.products);
        const incomeTaskValues = combinedIncome.map(item => item.done_works);

        const hasIncome = incomeValues.some(value => value > 0);
        
        setHasIncome(hasIncome);  
        updateChartData('daily', incomeData);
      } catch (error) {
        console.error('Error fetching income data:', error);
      }
    };

    fetchIncomeData();
  }, []);

  const updateChartData = (period, incomeData) => {
    const incomeValues = {
      daily: {
        all: incomeData.combined[0].dailyIncome.total,
        done: incomeData.combined[0].dailyIncome.done_works,
        prod: incomeData.combined[0].dailyIncome.products
      },
      weekly: {
        all: incomeData.combined[1].weeklyIncome.total,
        done: incomeData.combined[1].weeklyIncome.done_works,
        prod: incomeData.combined[1].weeklyIncome.products
      },
      monthly:{
        all: incomeData.combined[2].monthlyIncome.total,
        done: incomeData.combined[2].monthlyIncome.done_works,
        prod: incomeData.combined[2].monthlyIncome.products
      },
      yearly: {
        all: incomeData.combined[3].yearlyIncome.total,
        done: incomeData.combined[3].yearlyIncome.done_works,
        prod: incomeData.combined[3].yearlyIncome.products
      },
    };

    const incomeTransactionValues = {
      daily: {
        salery: incomeData.transactions[0].dailyIncome.saleryTransactionIncome,
        total: incomeData.transactions[0].dailyIncome.totalTransactionIncome,
        all: incomeData.transactions[0].dailyIncome.allTransactionIncome,
      },
      weekly: {
        salery: incomeData.transactions[1].weeklyIncome.saleryTransactionIncome,
        total: incomeData.transactions[1].weeklyIncome.totalTransactionIncome,
        all: incomeData.transactions[1].weeklyIncome.allTransactionIncome,
      },
      monthly:{
        salery: incomeData.transactions[2].monthlyIncome.saleryTransactionIncome,
        total: incomeData.transactions[2].monthlyIncome.totalTransactionIncome,
        all: incomeData.transactions[2].monthlyIncome.allTransactionIncome,
      },
      yearly: {
        salery: incomeData.transactions[3].yearlyIncome.saleryTransactionIncome,
        total: incomeData.transactions[3].yearlyIncome.totalTransactionIncome,
        all: incomeData.transactions[3].yearlyIncome.allTransactionIncome,
      },
    };

    const incomeProdValues = {
      daily: incomeData.products[0].dailyIncome.totalProductIncome,
      weekly: incomeData.products[1].weeklyIncome.totalProductIncome,
      monthly: incomeData.products[2].monthlyIncome.totalProductIncome,
      yearly: incomeData.products[3].yearlyIncome.totalProductIncome,
    };

    const incomeEarningsValues = {
      daily: incomeData.earnings[0].dailyIncome,
      weekly: incomeData.earnings[1].weeklyIncome,
      monthly: incomeData.earnings[2].monthlyIncome,
      yearly: incomeData.earnings[3].yearlyIncome,
    };

  const startOfWeek = moment().startOf('week').format('ll');
  const endOfWeek = moment().endOf('week').format('ll');
  const weekData = `${startOfWeek} - ${endOfWeek}`;

  const startOfMonth = moment().startOf('month').format('ll');
  const endOfMonth = moment().endOf('month').format('ll');
  const monthData = `${startOfMonth} - ${endOfMonth}`;

  const startOfYear = moment().startOf('year').format('ll');
  const endOfYear = moment().endOf('year').format('ll');
  const yearData = `${startOfYear} - ${endOfYear}`;

  const periodLabels = {
    daily: `Օրը ${moment().format('ll')}`,
    weekly: `Շաբաթը ${weekData}`,
    monthly: `Ամիս ${monthData}`,
    yearly: `Տարի ${yearData}`,
  };


    const chartData = {
      labels: [periodLabels.daily, periodLabels.weekly, periodLabels.monthly, periodLabels.yearly],
      datasets: [
        {
          label: 'Ընդհանուր Գործարքներ',
          data: [
            incomeValues.daily.all,
            incomeValues.weekly.all,
            incomeValues.monthly.all,
            incomeValues.yearly.all
          ],
          backgroundColor: '#1890ff',
          borderColor: '#1890ff',
          borderWidth: 1,
        },
        {
          label: 'Ապրանքների վաճառքից ստացաց եկամուտ',
          data: [incomeProdValues.daily, incomeProdValues.weekly, incomeProdValues.monthly, incomeProdValues.yearly],
          backgroundColor: '#ff4d4f',
          borderColor: '#ff4d4f',
          borderWidth: 1,
        },
        {
          label: 'Աշխատավարձ ըստ Ժամանակահատվածի',
          data: [
            incomeTransactionValues.daily.all,
            incomeTransactionValues.weekly.all,
            incomeTransactionValues.monthly.all,
            incomeTransactionValues.yearly.all
          ],
          backgroundColor: '#fa8c16',
          borderColor: '#fa8c16',
          borderWidth: 1,
        },
        {
          label: 'Եկամուտները ըստ Ժամանակահատվածի',
          data: [
            incomeEarningsValues.daily,
            incomeEarningsValues.weekly,
            incomeEarningsValues.monthly,
            incomeEarningsValues.yearly
          ],
          backgroundColor: '#52c41a',
          borderColor: '#52c41a',
          borderWidth: 1,
        },
      ],
    };

    const chartOptions = {
      plugins: {
        title: {
          display: true,
          text: '',
          font: {
            size: 16,
          },
        },
        tooltip: {
          callbacks: {
            afterBody: function (context) {
              const datasetLabel = context[0].dataset.label;
              
              if (datasetLabel === "Աշխատավարձ ըստ Ժամանակահատվածի") {
                const index = context[0].dataIndex;
    
                const periods = ['daily', 'weekly', 'monthly', 'yearly'];
                const period = periods[index];
      
                const salery = incomeTransactionValues[period].salery;
                const total = incomeTransactionValues[period].total;
      
                return [
                  `Եկամուտ արված աշտանաքից: ${total} դր.`,
                  `Աշխատավարձ: ${salery} դր.`,
                ];
              }

              if (datasetLabel !== 'Ընդհանուր Գործարքներ') {
                return;
              }

              const index = context[0].dataIndex;
    
              const periods = ['daily', 'weekly', 'monthly', 'yearly'];
              const period = periods[index];
    
              const done = incomeValues[period].done;
              const prod = incomeValues[period].prod;
    
              return [
                `Կատարված աշխատանքներ: ${done} դր.`,
                `Ապրանքների: ${prod} դր.`,
              ];
            }
          }
        }
      },
    };

    setData(chartData);
    setOptions(chartOptions);
  };

  if (!data) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <Bar data={data} options={options} />
    </div>
  );
};

export default DashboardIncomeChart;

